import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Delete, Edit } from '@mui/icons-material';
import * as Sentry from '@sentry/react';
import { ColDef, ICellRendererParams, ValueFormatterParams } from 'ag-grid-community';
import { getPrompts, deletePrompt, SystemPrompt } from '../../services/promptService';
import DataTable from '../shared/DataTable';
import ConfirmationModal from '../shared/ConfirmationModal';
import { useNotification } from '../../contexts/NotificationContext';
import { formatDateTime } from '../../utils/dateUtils';
import { SUB_FEATURES_CONFIG } from './index';

interface PromptsListComponentProps {
  feature: string;
  onEditPrompt: (prompt: SystemPrompt) => void;
  onCreatePrompt: () => void;
  refreshTrigger?: number;
}

export const PromptsListComponent: React.FC<PromptsListComponentProps> = ({
  feature,
  onEditPrompt,
  onCreatePrompt,
  refreshTrigger,
}) => {
  const { t } = useTranslation();
  const { showNotification } = useNotification();
  const [rowData, setRowData] = useState<SystemPrompt[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const translationPath = 'system_management.system_prompts';
  const availableSubFeatures = SUB_FEATURES_CONFIG[feature] || [];
  const hasSubFeatures = availableSubFeatures.length > 0;

  const columnDefs: ColDef<any>[] = [
    {
      field: 'name',
      headerName: t(`${translationPath}.name`),
      sortable: true,
      filter: true,
      cellStyle: {
        cursor: 'pointer',
        color: '#3b82f6',
      },
      onCellClicked: (params) => {
        onEditPrompt(params.data as SystemPrompt);
      },
    },
    {
      field: 'type',
      headerName: t(`${translationPath}.type`),
      sortable: true,
      filter: true,
      sort: 'desc',
      valueFormatter: (params: ValueFormatterParams<SystemPrompt>) => {
        return t(`${translationPath}.${params.data?.type}_prompt`);
      },
    },
    ...(hasSubFeatures
      ? [
          {
            field: 'sub_feature',
            headerName: t(`${translationPath}.sub_feature`),
            sortable: true,
            filter: true,
            valueFormatter: (params: ValueFormatterParams<SystemPrompt>) => {
              return params.data?.sub_feature
                ? t(`${translationPath}.sub_features.${params.data.sub_feature}`)
                : '-';
            },
          },
        ]
      : []),
    {
      field: 'is_active',
      headerName: t(`${translationPath}.active`),
      sortable: true,
      filter: true,
      cellRenderer: (params: ICellRendererParams<SystemPrompt>) => {
        return (
          <span
            className={`px-2 py-1 rounded-full text-xs ${
              params.data?.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
            }`}
          >
            {params.data?.is_active ? t('common.yes') : t('common.no')}
          </span>
        );
      },
    },
    {
      field: 'default_prompt',
      headerName: t(`${translationPath}.default`),
      sortable: true,
      filter: true,
      cellRenderer: (params: ICellRendererParams<SystemPrompt>) => {
        return (
          <span
            className={`px-2 py-1 rounded-full text-xs ${
              params.data?.default_prompt
                ? 'bg-blue-100 text-blue-800'
                : 'bg-gray-100 text-gray-800'
            }`}
          >
            {params.data?.default_prompt ? t('common.yes') : t('common.no')}
          </span>
        );
      },
    },
    {
      field: 'updated_at',
      headerName: t(`${translationPath}.last_updated`),
      sortable: true,
      filter: true,
      valueFormatter: (params: ValueFormatterParams<SystemPrompt>) => {
        return params.data?.updated_at ? formatDateTime(params.data.updated_at) : '-';
      },
    },
    {
      field: 'actions',
      headerName: t(`${translationPath}.actions`),
      sortable: false,
      filter: false,
      cellRenderer: (params: ICellRendererParams<SystemPrompt>) => {
        return (
          <div className="flex space-x-2">
            <button
              onClick={() => onEditPrompt(params.data as SystemPrompt)}
              className="text-blue-600 hover:text-blue-800"
              title={t(`${translationPath}.edit_prompt`)}
            >
              <Edit className="h-4 w-4" />
            </button>
            <button
              onClick={() => handleDeleteConfirm(params.data?.id as string)}
              className="text-red-600 hover:text-red-800"
              title={t(`${translationPath}.delete_prompt`)}
            >
              <Delete className="h-4 w-4" />
            </button>
          </div>
        );
      },
    },
  ];

  const gridConfig = {
    pagination: true,
    // paginationPageSize: 50,
    paginationAutoPageSize: true,
    defaultColDef: {
      flex: 1,
      minWidth: 100,
      resizable: true,
    },
  };

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const data = await getPrompts(feature);
      setRowData(data);
    } catch (err: any) {
      console.error('Error fetching prompts:', err);

      // Extract error message from API response
      let errorMessage = t(`${translationPath}.fetch_error`);
      if (err?.message) {
        errorMessage = err.message;
      }

      showNotification(errorMessage, 'error', 10000);
      Sentry.captureException(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [feature, refreshTrigger]);

  const handleDeleteConfirm = (id: string) => {
    setItemToDelete(id);
    setIsConfirmDialogOpen(true);
  };

  const handleDelete = async () => {
    if (itemToDelete === null) return;

    setIsDeleting(true);
    try {
      await deletePrompt(itemToDelete);
      showNotification(t(`${translationPath}.delete_success`), 'success');
      fetchData();
    } catch (error: any) {
      console.error('Error deleting prompt:', error);

      // Extract error message from API response
      let errorMessage = t(`${translationPath}.delete_error`);
      if (error?.message) {
        errorMessage = error.message;
      }

      showNotification(errorMessage, 'error', 10000);
    } finally {
      setIsDeleting(false);
      setIsConfirmDialogOpen(false);
      setItemToDelete(null);
    }
  };

  return (
    <div className="flex flex-col h-full" style={{ height: 'calc(100vh - 236px)' }}>
      <div className="flex justify-between items-center mb-1">
        <button
          style={{ position: 'absolute', top: '85px', right: '30px' }}
          onClick={onCreatePrompt}
          className="px-2 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          {t(`${translationPath}.create_prompt`)}
        </button>
      </div>

      <div className="bg-white rounded-lg shadow flex-grow min-h-0">
        <DataTable<SystemPrompt>
          rowData={rowData}
          columnDefs={columnDefs}
          gridConfig={gridConfig}
          isLoading={isLoading}
          theme="ag-theme-alpine"
          height={'100%'}
          noRowsMessage={t(`${translationPath}.no_prompts`)}
        />
      </div>

      <ConfirmationModal
        open={isConfirmDialogOpen}
        onClose={() => setIsConfirmDialogOpen(false)}
        onConfirm={handleDelete}
        title={t(`${translationPath}.confirm_delete`)}
        message={t(`${translationPath}.confirm_delete_message`)}
        isLoading={isDeleting}
      />
    </div>
  );
};

export default PromptsListComponent;
